// tippi/src/pages/carrier/VehicleDetail.js
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCarrierOrg } from '../../hooks/useCarrierOrg';
import { useOrgVehicle } from '../../hooks/useOrgVehicle';
import { useCarrierMembers } from '../../hooks/useCarrierMembers';
import { useVehicleServices } from '../../hooks/useVehicleServices';
import { carrierApi } from '../../firebase/functionsService';
import { BODY_TYPE_LABEL, FUEL_LABEL, inspectionStatus, vehicleAvailability } from '../../utils/vehicleStatus';
import VehicleForm from './VehicleForm';
import ServiceForm from './ServiceForm';
import Field from './Field';
import { ui, chip } from './carrierStyles';

function InspectionOverrideForm({ orgId, vehicleId }) {
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  async function submit(e) {
    e.preventDefault();
    setStatus('saving');
    setMessage('');
    try {
      await carrierApi.grantInspectionOverride({ orgId, vehicleId, until, reason });
      setStatus('saved');
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Kunne ikke lagre overstyringen');
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <p style={ui.hint}>Bilen kan ikke tildeles ordre med utløpt EU-kontroll. Som administrator kan du gi en midlertidig overstyring i opptil 30 dager. Den logges med begrunnelsen.</p>
      <Field id="o-until" label="Overstyr til dato">
        <input id="o-until" type="date" style={ui.input} value={until} onChange={e => setUntil(e.target.value)} />
      </Field>
      <Field id="o-reason" label="Begrunnelse">
        <input id="o-reason" style={ui.input} value={reason} onChange={e => setReason(e.target.value)} maxLength={500} />
      </Field>
      {status === 'error' && <p role="alert" style={ui.error}>{message}</p>}
      {status === 'saved' && <p role="status" style={ui.ok}>Overstyringen er lagret.</p>}
      <button type="submit" className="tippi-btn" style={{ ...ui.btn, marginTop: 12 }} disabled={status === 'saving'}>Gi overstyring</button>
    </form>
  );
}

export default function VehicleDetail() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { orgId, isStaff, isAdmin } = useCarrierOrg();
  const { vehicle, loading } = useOrgVehicle(vehicleId);
  const { members } = useCarrierMembers(orgId);
  const { services } = useVehicleServices(vehicleId);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');

  if (loading) return <p style={ui.hint}>Laster…</p>;
  if (!vehicle || vehicle.orgId !== orgId) {
    return <p style={ui.hint}>Fant ikke bilen. <Link to="/transportor/biler">Tilbake til bilene</Link></p>;
  }

  const drivers = members.filter(m => m.role === 'driver' && m.status === 'active');
  const defaultDriver = drivers.find(d => d.id === vehicle.defaultDriverUid);
  const availability = vehicleAvailability(vehicle);
  const inspection = inspectionStatus(vehicle);

  async function remove() {
    if (!window.confirm(`Slette ${vehicle.registrationPlate}? Servicehistorikken slettes også.`)) return;
    setError('');
    try {
      await carrierApi.deleteOrgVehicle({ orgId, vehicleId });
      navigate('/transportor/biler');
    } catch (err) {
      setError(err.message || 'Kunne ikke slette bilen');
    }
  }

  return (
    <>
      <p style={ui.hint}><Link to="/transportor/biler">‹ Biler</Link></p>
      <div style={{ ...ui.row, justifyContent: 'space-between' }}>
        <h1 style={ui.h1}>{vehicle.registrationPlate}</h1>
        <span style={chip(availability.ok ? 'ok' : 'expired')}>{availability.label}</span>
      </div>

      {editing ? (
        <div style={ui.card}>
          <VehicleForm
            vehicle={vehicle}
            orgId={orgId}
            drivers={drivers.map(d => ({ id: d.id, name: d.name }))}
            onSaved={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : (
        <div style={ui.card}>
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px 16px' }}>
            <div><dt style={ui.hint}>Merke</dt><dd style={{ margin: 0 }}>{vehicle.make}</dd></div>
            <div><dt style={ui.hint}>Biltype</dt><dd style={{ margin: 0 }}>{BODY_TYPE_LABEL[vehicle.bodyType] ?? vehicle.bodyType}</dd></div>
            <div><dt style={ui.hint}>Drivstoff</dt><dd style={{ margin: 0 }}>{FUEL_LABEL[vehicle.fuelType] ?? vehicle.fuelType}</dd></div>
            <div><dt style={ui.hint}>Aksler</dt><dd style={{ margin: 0 }}>{vehicle.axles}</dd></div>
            <div><dt style={ui.hint}>Egenvekt</dt><dd style={{ margin: 0 }}>{vehicle.curbWeightKg} kg</dd></div>
            <div><dt style={ui.hint}>Lovlig nyttelast</dt><dd style={{ margin: 0 }}>{vehicle.legalPayloadKg} kg</dd></div>
            <div><dt style={ui.hint}>Lovlig bruttovekt</dt><dd style={{ margin: 0 }}>{vehicle.legalGrossWeightKg} kg</dd></div>
            <div><dt style={ui.hint}>Standardsjåfør</dt><dd style={{ margin: 0 }}>{defaultDriver?.name ?? 'Ingen fast sjåfør'}</dd></div>
            <div><dt style={ui.hint}>Neste EU-kontroll</dt><dd style={{ margin: 0 }}>{vehicle.nextInspectionDate} <span style={chip(inspection.tone)}>{inspection.label}</span></dd></div>
            <div><dt style={ui.hint}>Neste service</dt><dd style={{ margin: 0 }}>{vehicle.nextServiceDate ?? 'Ikke satt'}{vehicle.nextServiceKm ? ` / ${vehicle.nextServiceKm} km` : ''}</dd></div>
          </dl>
          {vehicle.status === 'out_of_service' && (
            <p style={{ ...ui.error, marginTop: 14 }}>
              Ute av drift: {vehicle.outOfServiceReason}. Fra {vehicle.outOfServiceFrom}{vehicle.outOfServiceUntil ? ` til ${vehicle.outOfServiceUntil}` : ''}.
            </p>
          )}
          {isStaff && (
            <div style={{ ...ui.row, marginTop: 14 }}>
              <button type="button" style={ui.btnSecondary} onClick={() => setEditing(true)}>Rediger</button>
              {isAdmin && <button type="button" style={ui.btnDanger} onClick={remove}>Slett bil</button>}
            </div>
          )}
          {error && <p role="alert" style={ui.error}>{error}</p>}
        </div>
      )}

      {isAdmin && inspection.tone === 'expired' && (
        <div style={ui.card}>
          <h2 style={ui.h2}>Overstyr utløpt EU-kontroll</h2>
          {vehicle.inspectionOverrideUntil && <p style={ui.hint}>Gjeldende overstyring til {vehicle.inspectionOverrideUntil}: {vehicle.inspectionOverrideReason}</p>}
          <InspectionOverrideForm orgId={orgId} vehicleId={vehicleId} />
        </div>
      )}

      <div style={ui.card}>
        <h2 style={ui.h2}>Servicehistorikk</h2>
        {services.length === 0 && <p style={ui.hint}>Ingen servicer registrert.</p>}
        {services.map(s => (
          <div key={s.id} style={ui.listRow}>
            <span>
              <strong>{s.type}</strong> <span style={ui.hint}>{s.date}{s.odometerKm ? ` · ${s.odometerKm} km` : ''}</span>
              {s.note && <span style={{ display: 'block', ...ui.hint }}>{s.note}</span>}
            </span>
            <span style={ui.hint}>{s.costNok ? `${s.costNok} kr` : ''}</span>
          </div>
        ))}
      </div>

      {isStaff && (
        <div style={ui.card}>
          <h2 style={ui.h2}>Registrer service</h2>
          <ServiceForm orgId={orgId} vehicleId={vehicleId} />
        </div>
      )}
    </>
  );
}
